"use client";

import AddNewButton from "@/app/shared/add-new-button";
import SystemSettingsModal from "@/app/shared/modal-views/system-settings-modal";
import { routes } from "@/config/routes";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users";
import {
  PiMagnifyingGlassBold,
  PiSlidersHorizontal,
  PiGearBold,
} from "react-icons/pi";
import { Title, Input, Select, Button } from "rizzui";
import { useEffect, useState } from "react";
import { useModal } from "@/app/shared/modal-views/use-modal";
import PackageRepository from "@/utilities/repositories/Package";
import { IPackage } from "@/utilities/types/Package";
import PromotionRepository from "@/utilities/repositories/Promotion";
import { IPromotion } from "@/utilities/types/Promotion";
import { MultiSelect } from "rizzui";

type FilterElementProps = {
  onSearch: (searchTerm: string) => void;
  searchTerm: string;
  onPackageFilter?: (packageId?: string) => void;
  selectedPackage?: string | null;
  onModulesFilter?: (modules: ModuleType[]) => void;
  selectedModules?: ModuleType[];
  onPromotionFilter?: (promotionId?: string) => void;
  selectedPromotion?: string | null;
  variant?:
    | "users"
    | "unit-transaction"
    | "unit-type"
    | "site-content"
    | "enquiry-entries";
};

export type ModuleType = "LDP" | "Directory";

export default function FilterElement({
  onSearch,
  searchTerm,
  onPackageFilter,
  selectedPackage,
  onModulesFilter,
  selectedModules,
  onPromotionFilter,
  selectedPromotion,
  variant,
}: FilterElementProps) {
  const { profile } = useProfile();
  const { openModal } = useModal();
  const [packages, setPackages] = useState<IPackage[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [promotions, setPromotions] = useState<IPromotion[]>([]);
  const [isLoadingPromotions, setIsLoadingPromotions] = useState(false);
  const [showPackageFilter, setShowPackageFilter] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (variant !== "users") return;

      // Fetch packages
      setIsLoadingPackages(true);
      try {
        const packageResponse = await PackageRepository.getPackageActive();
        setPackages(packageResponse.data?.items || []);
      } catch (error) {
        console.error("Failed to fetch packages:", error);
      } finally {
        setIsLoadingPackages(false);
      }

      // Fetch promotions
      setIsLoadingPromotions(true);
      try {
        const promotionResponse = await PromotionRepository.getPromotionList(
          {},
        );
        setPromotions(promotionResponse.data?.items || []);
      } catch (error) {
        console.error("Failed to fetch promotions:", error);
      } finally {
        setIsLoadingPromotions(false);
      }
    };

    fetchData();
  }, [variant]);

  const packageOptions: { label: string; value: string }[] = [
    ...packages.map((pkg) => ({
      label: pkg.name,
      value: pkg.id,
    })),
  ];

  const moduleOptions: { label: string; value: ModuleType }[] = [
    { label: "Directory", value: "Directory" },
    { label: "LDP", value: "LDP" },
  ];

  const promotionOptions: { label: string; value: string }[] = [
    ...promotions.map((promotion) => ({
      label: `${promotion.code} - ${promotion.description || "No description"}`,
      value: promotion.id,
    })),
  ];

  return (
    <div className="mb-4">
      <div className="relative flex z-50 mb-4 w-full flex-col lg:flex-row flex-wrap items-center justify-between gap-2.5 @container">
        <div className="flex flex-1 w-full flex-row gap-2 items-center justify-between">
          <div className="flex items-center gap-3">
            <Title
              as="h5"
              className="@xl:basis-auto text-xl md:text-2xl flex-shrink-0"
            >
              All Users
            </Title>
            {variant === "users" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPackageFilter(!showPackageFilter)}
                className="flex items-center gap-1"
              >
                <PiSlidersHorizontal className="h-4 w-4" />
                Filter
              </Button>
            )}
          </div>
          {variant === "users" && (
            <div className="flex flex-row flex-shrink-0 justify-end md:hidden">
              <AddNewButton
                navigateTo={routes.user.create}
                className="max-w-max"
              >
                Create User
              </AddNewButton>
            </div>
          )}
        </div>

        {profile?.role === Roles.ADMIN && (
          <div className="w-full flex justify-start lg:w-fit">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                openModal({
                  view: <SystemSettingsModal />,
                  customSize: "600px",
                });
              }}
              className="flex items-center gap-1 min-h-10"
            >
              <PiGearBold className="h-4 w-4" />
              Global Settings
            </Button>
          </div>
        )}
        <div className="flex flex-1 w-full flex-col md:flex-row gap-2 justify-start lg:justify-end">
          <Input
            type="search"
            placeholder="Search by name, email..."
            value={searchTerm}
            onClear={() => onSearch("")}
            onChange={(event) => onSearch(event.target.value)}
            prefix={<PiMagnifyingGlassBold className="h-4 w-4" />}
            rounded="lg"
            clearable
            className="w-full"
            //className="w-full @xl:ms-auto ml-auto @xl:w-auto  @4xl:w-[230px] @5xl:w-auto"
          />
          {variant === "users" && (
            <div className="md:flex flex-row hidden justify-end flex-shrink-0 ">
              <AddNewButton
                navigateTo={routes.user.create}
                className="max-w-max"
              >
                Create User
              </AddNewButton>
            </div>
          )}
        </div>
      </div>
      {variant === "users" && showPackageFilter && (
        <div className="mt-2 flex flex-col md:flex-row gap-2">
          <Select
            placeholder="Filter by package"
            options={packageOptions}
            value={packageOptions.find(
              (option) => option.value === selectedPackage,
            )}
            onChange={(option: { value: string }) =>
              onPackageFilter?.(option.value)
            }
            onClear={() => onPackageFilter?.()}
            clearable
            className="w-full md:w-48"
            disabled={isLoadingPackages}
          />
          <MultiSelect
            placeholder="Filter by module"
            options={moduleOptions}
            value={(selectedModules as string[]) || []}
            onChange={(selectedValues: string[]) => {
              onModulesFilter?.(selectedValues as ModuleType[]);
            }}
            onClear={() => onModulesFilter?.([])}
            clearable
            className="w-full md:w-auto"
          />
          <Select
            placeholder="Filter by promotion"
            options={promotionOptions}
            value={promotionOptions.find(
              (option) => option.value === selectedPromotion,
            )}
            onChange={(option: { value: string }) =>
              onPromotionFilter?.(option.value)
            }
            onClear={() => onPromotionFilter?.()}
            clearable
            className="w-full md:w-48"
            disabled={isLoadingPromotions}
          />
        </div>
      )}
    </div>
  );
}
